/**
 * Integration domain event schemas.
 *
 * Typed payload validation for all integration-related Mycelium events
 * (n8n, Gmail, Xero sync).
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module integration.events
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Event schemas ───────────────────────────────────────────────────────

export const WorkflowIntelligenceUpdatedEvent = BackboneEventSchema.extend({
  domain: z.literal('integration'),
  type: z.literal('workflow_intelligence_updated'),
  payload: z.object({
    workflowId: z.string(),
    workflowName: z.string(),
    failureRate: z.number().optional(),
    trend: z.string().optional(),
    totalExecutions: z.number().optional(),
    averageDurationMs: z.number().optional(),
    source: z.string(),
  }),
})

export const GmailSchedulingDetectedEvent = BackboneEventSchema.extend({
  domain: z.literal('integration'),
  type: z.literal('gmail_scheduling_detected'),
  payload: z.object({
    source: z.string(),
    action: z.string(),
    messageId: z.string(),
    schedulingInfo: z.record(z.unknown()).optional(),
    email: z.object({
      subject: z.string().optional(),
      body: z.string().optional(),
      from: z.string().optional(),
      to: z.string().optional(),
    }).optional(),
  }),
})

export const XeroSyncRequiredEvent = BackboneEventSchema.extend({
  domain: z.literal('integration'),
  type: z.literal('xero_sync_required'),
  payload: z.object({
    invoiceId: z.string(),
    amount: z.number().optional(),
    action: z.string().optional(),
    source: z.string(),
  }),
})

/** Google Workspace activity → Mycelium (from IntegrationActivityService). */
export const GmailMessageReceivedEvent = BackboneEventSchema.extend({
  domain: z.literal('integration'),
  type: z.literal('gmail_message_received'),
  payload: z.object({
    activityId: z.string(),
    activityDomain: z.string().optional(),
    eventType: z.string().optional(),
    messageId: z.string().optional(),
    actionType: z.string().optional(),
    subject: z.string().optional(),
    fromAddress: z.string().optional(),
    source: z.string().optional(),
  }).passthrough(),
})

export const GmailMessageSentEvent = BackboneEventSchema.extend({
  domain: z.literal('integration'),
  type: z.literal('gmail_message_sent'),
  payload: z.object({
    activityId: z.string(),
    activityDomain: z.string().optional(),
    eventType: z.string().optional(),
    messageId: z.string().optional(),
    actionType: z.string().optional(),
    subject: z.string().optional(),
    source: z.string().optional(),
  }).passthrough(),
})

/**
 * Emitted when a Xero CRM sync conflict is resolved.
 */
export const XeroConflictResolvedEvent = BackboneEventSchema.extend({
  domain: z.literal('integration'),
  type: z.literal('xero_conflict_resolved'),
  payload: z.object({
    conflictId: z.string(),
    resolution: z.enum(['use_xero', 'use_crm', 'manual_merge']),
    source: z.string().optional(),
  }),
})

// ── All integration events ──────────────────────────────────────────────

export const IntegrationDomainEvents = [
  WorkflowIntelligenceUpdatedEvent,
  GmailSchedulingDetectedEvent,
  XeroSyncRequiredEvent,
  GmailMessageReceivedEvent,
  GmailMessageSentEvent,
  XeroConflictResolvedEvent,
] as const
